/**
 * Widget Layout
 * Root layout wrapper with proper overflow handling and navigation
 */

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutGrid } from 'lucide-react';
import { colors } from '@/design-system/theme/colors';

export function WidgetLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/templates', label: 'Templates', icon: LayoutGrid },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden" style={{ backgroundColor: colors.surfaceLightest }}>
      {/* Top Navigation */}
      <header
        className="shrink-0 h-14 shadow-sm z-10"
        style={{ backgroundColor: colors.white, borderBottom: `1px solid ${colors.surfaceSubtle}` }}
      >
        <div className="flex items-center justify-between h-full max-w-7xl mx-auto px-4 sm:px-6">
          {/* Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] select-none"
            onClick={() => navigate('/')}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)` }}
            >
              <span className="text-white font-bold text-base">N</span>
            </div>
            <span
              className="font-semibold text-base hidden md:block"
              style={{ color: colors.textInverse }}
            >
              Notifyy Widgets
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98] select-none"
                style={{
                  backgroundColor: isActive(item.path) ? colors.primaryExtraLight : 'transparent',
                  color: isActive(item.path) ? colors.primary : colors.textMuted,
                }}
                onClick={() => navigate(item.path)}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = colors.surfaceLighter;
                    e.currentTarget.style.color = colors.textDefault;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.textMuted;
                  }
                }}
              >
                <item.icon size={18} />
                <span className="hidden md:inline">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content with Scroll */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}

export default WidgetLayout;
